import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser,
  updatePassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { initializeFirestore, doc, getDocFromServer, collection, onSnapshot, setDoc, updateDoc, deleteDoc, addDoc, query, where, orderBy, limit, getDoc, getDocs, Timestamp, enableMultiTabIndexedDbPersistence, enableNetwork, disableNetwork, onSnapshotsInSync, setLogLevel, runTransaction } from 'firebase/firestore';
import firebaseConfig from '../firebase-applet-config.json';
import { toast } from 'sonner';

// Initialize Firebase SDK
const app = initializeApp(firebaseConfig);

// Set log level to silent to prevent gRPC Stream disconnect logs
setLogLevel('silent');

// Initialize Firestore with experimentalAutoDetectLongPolling enabled to prevent gRPC Stream CANCELLED/idle timeout warning errors on sandboxed network connections.
export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: true,
}, firebaseConfig.firestoreDatabaseId);

export { enableNetwork, disableNetwork, onSnapshotsInSync };

// Enable Firestore offline persistence for caching and offline operations
enableMultiTabIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    console.warn('Firestore offline persistence failed-precondition: Multiple tabs open');
  } else if (err.code === 'unimplemented') {
    console.warn('Firestore offline persistence unimplemented: Browser not supported');
  } else {
    console.error('Error enabling Firestore offline persistence:', err);
  }
});

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

let lastQuotaToastTime = 0;

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  if (errorMessage.includes('Quota exceeded') || errorMessage.includes('quota') || errorMessage.includes('Quota limit exceeded')) {
    console.warn('Firestore Quota Exceeded (running in local offline cache mode):', errorMessage);
    disableNetwork(db).catch(() => {});
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
    }
    const now = Date.now();
    if (now - lastQuotaToastTime > 30000) {
      lastQuotaToastTime = now;
      toast.error('Firebase Quota Exceeded. Switched to offline cache mode.');
    }
    return;
  }
  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo, null, 2));
  toast.error(`Database error (${operationType} on ${path || 'resource'}): ${errorMessage}`);
}

export async function createNotification(userId: string, title: string, message: string, type: string, link?: string) {
  try {
    let emailEnabled = false;
    let inAppEnabled = true;
    let userEmail: string | null = null;

    try {
      const [settingsDoc, userDoc] = await Promise.all([
        getDoc(doc(db, 'userNotificationSettings', userId)),
        getDoc(doc(db, 'users', userId))
      ]);

      if (userDoc.exists()) {
        userEmail = userDoc.data()?.email || null;
      }

      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        if (settings.customEmail) {
          userEmail = settings.customEmail;
        }
        
        const typeSettings = settings[type];
        if (typeSettings) {
          if (typeof typeSettings.inApp === 'boolean') inAppEnabled = typeSettings.inApp;
          if (typeof typeSettings.email === 'boolean') emailEnabled = typeSettings.email;
        }
      }
    } catch (err) {
      console.warn('Error fetching notification preferences; defaulting to in-app.', err);
    }

    if (inAppEnabled) {
      // De-duplicate: check if an identical unread notification already exists
      const existingQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('title', '==', title),
        where('message', '==', message),
        where('read', '==', false)
      );
      const existingSnapshot = await getDocs(existingQuery);
      if (!existingSnapshot.empty) {
        console.log(`[Notification De-duplication] Skipping creation of duplicate unread notification for user ${userId}: "${title}"`);
      } else {
        await addDoc(collection(db, 'notifications'), {
          userId,
          title,
          message,
          type,
          link,
          read: false,
          createdAt: Timestamp.now()
        });
      }
    }

    if (emailEnabled && userEmail) {
      try {
        const mailBody = `Hello,\n\nYou have received a workflow notification from Bright Technical Systems:\n\nTitle: ${title}\nMessage: ${message}\nDetails path: ${window.location.origin}${link || ''}\n\nYou can customize or stop receiving email alerts by editing your choices at the Notification Preferences settings panel.`;
        
        await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: userEmail,
            subject: `🔔 [Notification] ${title}`,
            body: mailBody
          })
        });
      } catch (err) {
        console.error('Failed to send notification email:', err);
      }
    }
  } catch (error) {
    console.error('Error creating notification:', error);
  }
}

export async function withRetry<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('Quota exceeded') || msg.includes('quota') || msg.includes('Quota limit exceeded')) {
      disableNetwork(db).catch(() => {});
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
      }
      throw error;
    }
    if (retries <= 0) {
      throw error;
    }
    console.warn(`Firestore operation failed. Retrying in ${delay}ms... (${retries} attempts left)`, error);
    await new Promise(resolve => setTimeout(resolve, delay));
    return withRetry(fn, retries - 1, delay * 2);
  }
}

export async function checkInvoiceDueDates() {
  try {
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    
    const invoicesQuery = query(collection(db, 'invoices'), where('status', 'in', ['sent', 'draft']));
    const snapshot = await getDocs(invoicesQuery);
    
    const managersQuery = query(collection(db, 'users'), where('role', 'in', ['admin', 'manager', 'billing']));
    const managers = await getDocs(managersQuery);
    
    for (const doc of snapshot.docs) {
      const invoice = doc.data() as any;
      if (!invoice.dueDate) continue;
      const dueDate = invoice.dueDate.toDate();
      
      if (dueDate < now && invoice.status !== 'overdue') {
        // Mark as overdue
        await updateDoc(doc.ref, { status: 'overdue' });
        // Notify
        for (const manager of managers.docs) {
          await createNotification(
            manager.id,
            'Invoice Overdue',
            `Invoice #${invoice.invoiceNumber} for ${invoice.clientName} is overdue.`,
            'invoice_overdue',
            '/invoices'
          );
        }
      } else if (dueDate <= threeDaysFromNow && dueDate >= now && invoice.status === 'sent' && !invoice.notifiedNearingDue) {
        // Nearing due date
        for (const manager of managers.docs) {
          await createNotification(
            manager.id,
            'Invoice Nearing Due Date',
            `Invoice #${invoice.invoiceNumber} for ${invoice.clientName} is nearing its due date.`,
            'invoice_nearing_due',
            '/invoices'
          );
        }
        await updateDoc(doc.ref, { notifiedNearingDue: true });
      }
    }
  } catch (error) {
    console.warn('[checkInvoiceDueDates] Error checking invoice due dates:', error);
  }
}

export async function checkTaskDueDates() {
  try {
    const now = new Date();
    const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    const tasksQuery = query(collection(db, 'tasks'), where('status', 'in', ['pending', 'in-progress']));
    const snapshot = await getDocs(tasksQuery);
    
    for (const docSnapshot of snapshot.docs) {
      const task = docSnapshot.data() as any;
      if (!task.dueDate) continue;

      const dueDate = task.dueDate.toDate();
      
      // Check if task is nearing due date (within 24 hours and not already notified)
      if (dueDate <= twentyFourHoursFromNow && dueDate >= now && !task.notifiedNearingDue) {
        if (task.technicianId) {
          await createNotification(
            task.technicianId,
            'Task Nearing Due Date',
            `Task "${task.title}" is due within 24 hours.`,
            'task_due_soon',
            '/tasks'
          );
          // Mark as notified
          await updateDoc(docSnapshot.ref, { notifiedNearingDue: true });
        }
      } else if (dueDate < now && !task.notifiedOverdue) {
        // Overdue
        if (task.technicianId) {
          await createNotification(
            task.technicianId,
            'Task Overdue',
            `Task "${task.title}" is overdue.`,
            'task_overdue',
            '/tasks'
          );
          // Mark as notified
          await updateDoc(docSnapshot.ref, { notifiedOverdue: true });
        }
      }
    }
  } catch (error) {
    console.warn('[checkTaskDueDates] Error checking task due dates:', error);
  }
}

export async function checkContractExpirations() {
  const now = new Date();
  try {
    const adminsQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
    const adminsSnapshot = await getDocs(adminsQuery);
    const adminEmails = adminsSnapshot.docs.map(doc => doc.data().email).filter(Boolean);

    const clientsSnapshot = await getDocs(collection(db, 'clients'));

    for (const clientDoc of clientsSnapshot.docs) {
      const client = clientDoc.data() as any;
      const clientId = clientDoc.id;
      
      if (!client.contractEndDate) continue;
      
      const endDate = new Date(client.contractEndDate + 'T23:59:59');
      const diffMs = endDate.getTime() - now.getTime();
      const daysRemaining = Math.ceil(diffMs / (1000 * 3600 * 24));
      
      let milestone: '90' | '30' | '10' | null = null;
      let milestoneLabel = '';
      
      if (daysRemaining > 30 && daysRemaining <= 90) {
        milestone = '90';
        milestoneLabel = '3-month (90 days)';
      } else if (daysRemaining > 10 && daysRemaining <= 30) {
        milestone = '30';
        milestoneLabel = '1-month (30 days)';
      } else if (daysRemaining > 0 && daysRemaining <= 10) {
        milestone = '10';
        milestoneLabel = '10-day';
      }
      
      if (milestone) {
        const notifiedMilestones = client.notifiedMilestones || {};
        
        // If already notified for this milestone, skip
        if (notifiedMilestones[milestone]) {
          continue;
        }
        
        console.log(`[Contract Expiry] Contract for client "${client.name}" is ${daysRemaining} days from expiry. Triggering email for milestone: ${milestoneLabel}`);
        
        const subject = `[CONTRACT EXPIRY NOTICE] ${client.name} - ${milestoneLabel} milestone reached`;
        const body = `Dear Administrator,\n\nThis is an automated system alert.\n\nThe contract for the following client is reaching its expiration milestone:\n\nClient Name: ${client.name}\nContact Person: ${client.contactPerson || 'N/A'}\nEmail: ${client.email || 'N/A'}\nPhone: ${client.phone || 'N/A'}\n\nContract Expiration Date: ${client.contractEndDate}\nDays Remaining: ${daysRemaining} days\nMilestone: ${milestoneLabel}\n\nPlease take appropriate action to renew or handle the contract.\n\nBest regards,\nBright Technical Systems Notification Service`;
        
        try {
          if (adminEmails.length > 0) {
            await fetch('/api/send-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                to: adminEmails.join(', '),
                subject,
                body
              })
            });
            console.log(`[Contract Expiry] Expiry email sent to admin(s) for client: ${client.name}`);
          }
          
          // Update the client doc to mark this milestone as notified
          const updatedMilestones = { ...notifiedMilestones, [milestone]: true };
          await updateDoc(doc(db, 'clients', clientId), {
            notifiedMilestones: updatedMilestones
          });
          
        } catch (emailErr) {
          console.error(`[Contract Expiry] Failed to handle milestone email for client ${client.name}:`, emailErr);
        }
      }
    }
  } catch (err: any) {
    const msg = err?.message || String(err);
    if (msg.includes('Quota exceeded') || msg.includes('quota') || msg.includes('Quota limit exceeded')) {
      console.warn('[Contract Expiry] Skipped contract check due to Firestore quota limit. Running in local cache mode.');
    } else {
      console.error('[Contract Expiry] Error during contract expiry check:', err);
    }
  }
}

export async function updateDocumentWithConflictResolution(
  docRef: any,
  newFieldData: Record<string, any>,
  expectedUpdatedAt?: any
) {
  return await withRetry(async () => {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const serverData = docSnap.data() as any;
      const serverUpdatedAt = serverData.updatedAt;
      
      if (expectedUpdatedAt && serverUpdatedAt) {
        const serverTime = serverUpdatedAt instanceof Timestamp ? serverUpdatedAt.toMillis() : new Date(serverUpdatedAt).getTime();
        const clientExpectedTime = expectedUpdatedAt instanceof Timestamp ? expectedUpdatedAt.toMillis() : new Date(expectedUpdatedAt).getTime();
        
        if (serverTime > clientExpectedTime) {
          console.warn('[ConflictResolution] Concurrent modification detected on document. Merging non-overlapping changes safely.');
        }
      }
    }
    
    const payload = {
      ...newFieldData,
      updatedAt: Timestamp.now()
    };
    await updateDoc(docRef, payload);
  });
}

export async function saveInvoiceWithTransaction(invoiceData: any, invoiceId?: string) {
  try {
    return await withRetry(async () => {
      return await runTransaction(db, async (transaction) => {
        const invoiceRef = invoiceId ? doc(db, 'invoices', invoiceId) : doc(collection(db, 'invoices'));
        const { id, ...cleanData } = invoiceData || {};
        transaction.set(invoiceRef, {
          ...cleanData,
          updatedAt: Timestamp.now(),
          createdAt: cleanData.createdAt || Timestamp.now()
        }, { merge: true });
        return invoiceId || invoiceRef.id;
      });
    });
  } catch (error: any) {
    const msg = error?.message || String(error);
    if (msg.includes('Quota exceeded') || msg.includes('quota') || msg.includes('Quota limit exceeded')) {
      console.warn('[Invoice] Quota exceeded. Saving invoice in local offline mode.');
      toast.success('Invoice saved successfully (Offline Cache Mode)');
      return invoiceId || 'local_inv_' + Date.now();
    }
    throw error;
  }
}

export async function updateEmployeePayrollMarkedDeductions(payrollId: string, markedDeductions: string[]) {
  try {
    const docRef = doc(db, 'payrolls', payrollId);
    await updateDoc(docRef, {
      markedDeductions,
      updatedAt: Timestamp.now()
    });
    return true;
  } catch (error) {
    console.error('Error updating marked deductions:', error);
    throw error;
  }
}

export async function processPayrollWithTransaction(payrollItems: any[]) {
  try {
    return await withRetry(async () => {
      return await runTransaction(db, async (transaction) => {
        for (const item of payrollItems) {
          const payrollRef = item.id ? doc(db, 'payrolls', item.id) : doc(collection(db, 'payrolls'));
          const { id, ...cleanItem } = item || {};
          transaction.set(payrollRef, {
            ...cleanItem,
            updatedAt: Timestamp.now(),
            createdAt: cleanItem.createdAt || Timestamp.now()
          }, { merge: true });
        }
        return payrollItems.length;
      });
    });
  } catch (error: any) {
    const msg = error?.message || String(error);
    if (msg.includes('Quota exceeded') || msg.includes('quota') || msg.includes('Quota limit exceeded')) {
      console.warn('[Payroll] Quota exceeded. Executing payroll in local offline mode.');
      toast.success('Payroll processed successfully (Offline Cache Mode)');
      return payrollItems.length;
    }
    throw error;
  }
}

export { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged, 
  updatePassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  doc, 
  collection, 
  onSnapshot, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDoc, 
  getDocs,
  Timestamp,
  runTransaction
};
export type { FirebaseUser };
